import type { FormEvent } from 'react'

import { useState } from 'react'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { ActionButton } from '@/components/generals/button'

type Status = 'idle' | 'loading' | 'success' | 'error'

type NewsletterFormProps = {
    className?: string
    source?: string
}

export const NewsletterForm = ({ className, source = 'footer' }: NewsletterFormProps) => {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'loading') {
            return
        }

        setStatus('loading')
        setMessage('')

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, source })
            })

            const data = await response.json()

            if (!response.ok || !data.ok) {
                setStatus('error')
                setMessage(data.message ?? 'Something went wrong. Please try again.')

                return
            }

            setStatus('success')
            setMessage(data.message)
            setEmail('')
        } catch {
            setStatus('error')
            setMessage('Something went wrong. Please try again.')
        }
    }

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            className={cn('flex w-full max-w-[420px] flex-col gap-2.5', className)}
        >
            <div className='flex w-full flex-col gap-2.5 sm:flex-row'>
                <label
                    htmlFor='newsletter-email'
                    className='sr-only'
                >
                    Your email address
                </label>

                <input
                    id='newsletter-email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    required
                    value={email}
                    onChange={(event) => {
                        setEmail(event.target.value)
                        setStatus('idle')
                    }}
                    placeholder='Your Email Address'
                    aria-invalid={status === 'error'}
                    className='h-11 w-full flex-1 rounded-xl bg-surface-3 px-4 text-[15px] tracking-[-0.01em] text-white ring-1 ring-white/10 transition-shadow duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] outline-none placeholder:text-white/40 focus:ring-white/30'
                />

                <ActionButton
                    type='submit'
                    size='small'
                    disabled={status === 'loading'}
                    className='h-11 shrink-0 px-4'
                >
                    {status === 'loading' ? (
                        <Loader2
                            aria-hidden='true'
                            className='size-4 animate-spin'
                        />
                    ) : null}

                    {status === 'loading' ? 'Signing up' : 'Get Notified'}
                </ActionButton>
            </div>

            <p
                role='status'
                aria-live='polite'
                className={cn(
                    'min-h-5 text-[13px] tracking-[-0.01em]',
                    status === 'error' ? 'text-[#ff6a5c]' : 'text-white/56'
                )}
            >
                {message}
            </p>
        </form>
    )
}
